// ─────────────────────────────────────────────────────────────────────────────
// CustomersPage, the public /customers page.
//
// This page used to be a logo wall, three testimonials and a case study, all of
// which were invented. None of it is here now. Instead of "who uses this", the
// page answers "who this is built for": the shape of the operator it fits, the
// weeks that send someone looking, what the first month actually looks like,
// and a plain list of the things Wavelength does not claim.
//
// The candour is deliberate and sits high on the page. Wavelength is early, and
// saying so, with references offered on a call, reads better than a fabricated
// customer list. Where a case study belongs there is a captioned <MediaSlot>
// holding the space until a real one is written and approved.
//
// Layout follows the shared marketing kit: left-aligned headings that end in a
// period, hairline borders, oversized radii, alternating white and grey
// sections, one dark strip, and a dark closing CTA.
// ─────────────────────────────────────────────────────────────────────────────
import { Link } from 'react-router-dom';
import {
    BTN_PRIMARY,
    BTN_SECONDARY,
    Icon,
    Container,
    Section,
    Heading,
    Lead,
    Body,
    Pill,
    MediaSlot,
    SectionHead,
    FeatureRow,
    FeatureGrid,
    SpecList,
    Steps,
    CTAStrip,
    CTASection,
    PageHero,
    MarketingShell,
    Seo,
} from '../../components/marketing/MarketingKit';
import { Reveal } from '../../components/marketing/Motion';

// ── The shape of the operator, as chips under the hero ────────────────────────
const PROFILE_CHIPS = [
    'Two to twenty locations',
    'A counter that weighs',
    'Wholesale accounts',
    'An online store',
    'Cash that still matters',
];

// ── The fit, said plainly ─────────────────────────────────────────────────────
const PROFILE = [
    {
        icon: 'pin',
        title: 'Two to twenty locations',
        body: 'Enough stores that a spreadsheet per site stops working, few enough that you still know every manager by name.',
    },
    {
        icon: 'scale',
        title: 'A counter that weighs and packs',
        body: 'Product sold by the pound, cut and packed in the store, priced on a scale rather than scanned off a shelf.',
    },
    {
        icon: 'receipt',
        title: 'A wholesale side',
        body: 'Accounts that buy on terms at their own price level and expect an invoice, a credit when something goes back, and a statement.',
    },
    {
        icon: 'cart',
        title: 'An online store',
        body: 'Orders arriving from the website that have to land in the same day, the same tenders and the same reconciliation as the counter.',
    },
    {
        icon: 'cash',
        title: 'Cash that still matters',
        body: 'A real share of takings in notes, moving from drawer to drop to safe to deposit, with a different person at every step.',
    },
    {
        icon: 'users',
        title: 'A back office of one or two',
        body: 'No controller and no analyst. The person closing the books is also the person answering the phone at four in the afternoon.',
    },
];

// ── The weeks that send someone looking ───────────────────────────────────────
const PROBLEMS = [
    {
        title: 'The day closes in a notebook',
        body: 'Register totals, drawer counts and the deposit written on paper, then keyed in days later by somebody who was not standing there.',
    },
    {
        title: 'A drawer is short and nobody owns it',
        body: 'The count agrees with the expected total because the expected total was on the screen while it was being counted.',
    },
    {
        title: 'The bank is a month behind',
        body: 'Payouts, deposits and sales get reconciled when there is time, which means at month end, which is too late to ask anyone what happened.',
    },
    {
        title: 'Every source tells a different story',
        body: 'The register report, the online store, the processor and the bank each hold their own idea of what a trading day was worth.',
    },
    {
        title: 'Wholesale invoices go out late',
        body: 'The order is picked and delivered, the invoice is written from memory a week later, and the receivable ages quietly in between.',
    },
    {
        title: 'Stock is a number somebody typed',
        body: 'On hand was last true at the last count, and every ordering decision since then has been a guess wearing the clothes of a figure.',
    },
];

// ── The first month, week by week ─────────────────────────────────────────────
const FIRST_MONTH = [
    {
        title: 'One location, connected',
        body: 'We point Wavelength at a single store and its online orders. Products, staff and order history come across on their own, and you pick who is allowed to see what.',
    },
    {
        title: 'One day, closed together',
        body: 'We run a full close with the manager who will own it: the blind count, the deposit, the written reason on a variance, the sign-off, and the day locking behind you.',
    },
    {
        title: 'One statement, imported',
        body: 'You export a month of activity from your bank and import the file. It matches against sales and processor payouts, and what does not match becomes your real exception list.',
    },
    {
        title: 'The rest of the stores',
        body: 'Remaining locations come on one at a time, in your order, on your days. Nothing has to land at once and nothing has to happen on a weekend.',
    },
    {
        title: 'The parts around the edges',
        body: 'Scales and label formats, receiving at the back door, purchasing and reorder points, wholesale price levels and invoicing. Whichever of them you actually use.',
    },
];

// ── What we do not claim, in writing ──────────────────────────────────────────
const HONEST_SPEC = [
    { label: 'Bank data', value: 'Statement CSV import, not a live bank feed' },
    { label: 'Card processing', value: 'Datacap transactions and settlement batches' },
    { label: 'Your register', value: 'We work alongside it, and add sources on request' },
    { label: 'Accounting', value: 'QuickBooks journal entry CSV, not an API connection' },
    { label: 'Margin and cost of goods', value: 'Not tracked' },
    { label: 'Billing', value: 'Plans are still set up by hand' },
    { label: 'Pricing', value: 'Published, on the pricing page' },
    { label: 'References', value: 'Introduced on a call, never printed as a logo' },
];

// ── The honest disqualifiers ──────────────────────────────────────────────────
const NOT_FOR = [
    {
        title: 'A single site with one drawer',
        body: 'One location, one register and no wholesale can be run from the register reports and a folder. Wavelength earns its keep when there is a second set of numbers to make agree with the first.',
    },
    {
        title: 'Anyone shopping for a new register',
        body: 'We are not a point of sale and are not trying to become one. Your staff keep ringing sales on whatever they use today, and the back office changes underneath them.',
    },
    {
        title: 'A team that needs margin reporting',
        body: 'Cost of goods, margin and true net revenue are not tracked. If that is the question you are trying to answer this quarter, we are not yet the thing that answers it.',
    },
];

const PAGE_DESCRIPTION =
    'Wavelength fits operators with two to twenty locations, a counter that weighs and packs, a wholesale side and an online store. See the fit and the first month.';

// ── Page ─────────────────────────────────────────────────────────────────────
export default function CustomersPage() {
    return (
        <MarketingShell>
            <Seo
                title="Who we build for and what the first month looks like"
                description={PAGE_DESCRIPTION}
                path="/customers"
                keywords="back office for multi location retail, software for two to twenty locations, cash control software, daily close software, wholesale invoicing software, retail reconciliation software, back office for a counter that weighs"
                jsonLd={{
                    '@context': 'https://schema.org',
                    '@graph': [
                        {
                            '@type': 'BreadcrumbList',
                            '@id': 'https://wavelengtherp.com/customers#breadcrumb',
                            itemListElement: [
                                { '@type': 'ListItem', position: 1, name: 'Home', item: 'https://wavelengtherp.com/' },
                                { '@type': 'ListItem', position: 2, name: 'Who we build for', item: 'https://wavelengtherp.com/customers' },
                            ],
                        },
                        {
                            '@type': 'WebPage',
                            '@id': 'https://wavelengtherp.com/customers#webpage',
                            url: 'https://wavelengtherp.com/customers',
                            name: 'Who Wavelength is built for',
                            description: PAGE_DESCRIPTION,
                            breadcrumb: { '@id': 'https://wavelengtherp.com/customers#breadcrumb' },
                            publisher: { '@id': 'https://wavelengtherp.com/#organization' },
                            about: { '@id': 'https://wavelengtherp.com/#organization' },
                        },
                    ],
                }}
            />

            {/* ── Hero ───────────────────────────────────────────────────── */}
            <PageHero
                eyebrow="Who we build for"
                title="The operator this is built for."
                lead="Wavelength was written for one kind of business rather than for everyone. This page describes that business, the weeks that send someone looking for us, and what the first month is actually like."
                primary={{ label: 'Book a walkthrough', to: '/contact' }}
                secondary={{ label: 'See the platform', to: '/features' }}
            >
                <div className="flex flex-col gap-8">
                    <div className="flex flex-wrap gap-2">
                        {PROFILE_CHIPS.map((c) => <Pill key={c}>{c}</Pill>)}
                    </div>
                    <MediaSlot
                        kind="Photo"
                        label="A service counter at closing time, one manager and a tablet"
                        note="Wide, shot from the customer side, lights half down, nobody posed"
                        ratio="16 / 7"
                        className="!rounded-[28px]"
                    />
                </div>
            </PageHero>

            {/* ── The candour strip, high on the page on purpose ──────────── */}
            <Section pad="sm">
                <Container>
                    <Reveal>
                        <CTAStrip
                            title="We are early, and we would rather say so."
                            body="There is no logo wall on this page and no numbers we cannot stand behind. When there is a customer story worth publishing, we will publish it with the operator's name on it. Until then, ask on a call and we will introduce you to a reference directly."
                            primary={{ label: 'Ask for a reference', to: '/contact' }}
                            secondary={{ label: 'See the platform', to: '/features' }}
                        />
                    </Reveal>
                </Container>
            </Section>

            {/* ── The shape of the fit ───────────────────────────────────── */}
            <Section id="fit" tone="paper2" pad="lg">
                <Container>
                    <Reveal>
                        <SectionHead
                            title="The shape of the fit."
                            lead="If most of the list below is you, the product will read like it was written for your week, because it was. If none of it is you, we will say so on the first call."
                            maxWidth="max-w-[540px]"
                        />
                    </Reveal>
                    <Reveal delay={80}>
                        <FeatureGrid className="mt-12" items={PROFILE} cols={3} />
                    </Reveal>
                </Container>
            </Section>

            {/* ── What brings someone here ───────────────────────────────── */}
            <Section id="problems" pad="lg">
                <Container>
                    <Reveal>
                        <SectionHead
                            title="What usually brings someone here."
                            lead="Nobody goes looking for back office software on a good week. These are the six weeks people describe on the first call, almost word for word."
                            maxWidth="max-w-[520px]"
                        />
                    </Reveal>
                    <Reveal delay={80}>
                        <FeatureGrid className="mt-12" items={PROBLEMS} cols={2} numbered />
                    </Reveal>
                </Container>
            </Section>

            {/* ── Where a case study goes ────────────────────────────────── */}
            <Section id="stories" tone="paper2" pad="lg">
                <Container>
                    <Reveal>
                        <FeatureRow
                            eyebrow="Customer stories"
                            title="This is where a customer story goes."
                            body="One will sit here when there is one worth reading: written with the operator, checked by them, and carrying figures they are willing to put their own name beside. The panel stays grey until that day rather than holding a story we made up."
                            bullets={[
                                'Written with the operator rather than about them',
                                'Numbers they have checked and agreed to publish',
                                'Named, or not published at all',
                                'References given on a call in the meantime',
                            ]}
                            cta={<Link to="/contact" className={BTN_SECONDARY}>Ask for a reference<Icon name="arrow" className="h-4 w-4" /></Link>}
                            media={
                                <MediaSlot
                                    kind="Photo"
                                    label="Customer story, once one is written and approved"
                                    note="A portrait behind the counter and the figures the operator agreed to share, side by side"
                                    ratio="4 / 3"
                                    className="!rounded-[28px]"
                                />
                            }
                        />
                    </Reveal>
                </Container>
            </Section>

            {/* ── The first month ────────────────────────────────────────── */}
            <Section id="first-month" pad="lg">
                <Container>
                    <div className="grid grid-cols-1 gap-10 lg:grid-cols-[380px_1fr] lg:gap-16">
                        <Reveal>
                            <div className="flex flex-col gap-4 lg:sticky lg:top-28">
                                <Heading>What the first month looks like.</Heading>
                                <Lead>
                                    No rip and replace, no freeze on trading, no consultant living in your office. One store, one day, one statement, then the rest at your pace.
                                </Lead>
                                <div className="mt-2">
                                    <Link to="/contact" className={BTN_PRIMARY}>Book a walkthrough<Icon name="arrow" className="h-4 w-4" /></Link>
                                </div>
                            </div>
                        </Reveal>
                        <Reveal delay={80}>
                            <Steps items={FIRST_MONTH} />
                        </Reveal>
                    </div>
                </Container>
            </Section>

            {/* ── What we will not claim ─────────────────────────────────── */}
            <Section id="claims" tone="paper2" pad="lg">
                <Container>
                    <Reveal>
                        <SectionHead
                            title="What we will not claim."
                            lead="The quickest way to judge a young product is to ask what it cannot do and watch how long the answer takes. Here is ours in writing, before you ask."
                            maxWidth="max-w-[540px]"
                        />
                    </Reveal>
                    <Reveal delay={80}>
                        <SpecList className="mt-12 max-w-[760px]" rows={HONEST_SPEC} />
                    </Reveal>
                    <Reveal delay={120}>
                        <Body className="mt-5 max-w-[600px]">
                            Everything else on this site describes something that runs today. If a page ever claims otherwise, tell us and we will take it down.
                        </Body>
                    </Reveal>
                </Container>
            </Section>

            {/* ── Who it is not for ──────────────────────────────────────── */}
            <Section id="not-for" pad="lg">
                <Container>
                    <Reveal>
                        <SectionHead
                            title="Who it is not for."
                            lead="Turning down a bad fit on the first call is cheaper for both of us than discovering it in month three."
                            maxWidth="max-w-[500px]"
                        />
                    </Reveal>
                    <Reveal delay={80}>
                        <div className="mt-12 grid grid-cols-1 overflow-hidden rounded-[24px] border border-[rgba(10,10,10,0.10)] bg-white sm:grid-cols-3">
                            {NOT_FOR.map((x, i) => (
                                <div key={x.title} className={`flex flex-col gap-3 p-7 ${i > 0 ? 'border-t border-[rgba(10,10,10,0.08)] sm:border-l sm:border-t-0' : ''}`}>
                                    <span className="text-[13px] font-medium tabular-nums text-[#A3A3A3]">{String(i + 1).padStart(2, '0')}</span>
                                    <div className="text-[17px] font-semibold tracking-[-0.015em] text-[#0A0A0A]">{x.title}</div>
                                    <Body>{x.body}</Body>
                                </div>
                            ))}
                        </div>
                    </Reveal>
                </Container>
            </Section>

            {/* ── Closing CTA ────────────────────────────────────────────── */}
            <CTASection
                eyebrow="Talk to us"
                title="Bring one trading day."
                body="What the registers rang, what the processor says it paid and what the bank actually received. We will walk the three of them through with you and tell you plainly whether this fits."
                primary={{ label: 'Book a walkthrough', to: '/contact' }}
                secondary={{ label: 'See the platform', to: '/features' }}
            />
        </MarketingShell>
    );
}
